/**
 * Admin — Estimate Shares (tracking).
 *
 * Every share a customer sent to a retailer / installer location: who, where, when,
 * how it was delivered (form handler vs. SMTP, redirected or not), whether it was
 * opened, how many replies, and the full event log per share. Super admins can
 * resend, restore a removed share, remove one, or send a copy to themselves via
 * SMTP without touching Pardot.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Search, Send, Eye, EyeOff, MessageSquare, ChevronLeft, ChevronRight, RefreshCw, RotateCcw, Trash2,
  ExternalLink, ListTree, AlertTriangle, CheckCircle2, XCircle, Clock, Store, HardHat, Loader2, Mail,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  fetchAdminShares, fetchAdminShare, adminShareAction,
  type AdminShare, type AdminSharesQuery, type AdminShareStats, type ShareEvent, type SharePagination,
} from '../../services/adminSharesApi';

const fmt = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const SendBadge: React.FC<{ s: AdminShare }> = ({ s }) => {
  if (s.lastSendStatus === 'ok') return <span className="badge text-bg-success">{s.redirected ? 'sent · redirected' : 'sent'}</span>;
  if (s.lastSendStatus === 'failed') return <span className="badge text-bg-danger">failed</span>;
  if (s.lastSendStatus === 'skipped') return <span className="badge text-bg-warning text-dark">skipped</span>;
  return <span className="badge text-bg-secondary">not sent</span>;
};

const EventIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'send_ok' || type === 'restored') return <CheckCircle2 size={14} className="text-success" />;
  if (type === 'send_failed' || type === 'removed') return <XCircle size={14} className="text-danger" />;
  if (type === 'send_skipped' || type === 'location_link_ambiguous') return <AlertTriangle size={14} className="text-warning" />;
  if (type === 'viewed') return <Eye size={14} className="text-primary" />;
  if (type === 'comment') return <MessageSquare size={14} className="text-primary" />;
  return <Clock size={14} className="text-muted" />;
};

export const AdminShares: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const [shares, setShares] = useState<AdminShare[]>([]);
  const [stats, setStats] = useState<AdminShareStats | null>(null);
  const [pagination, setPagination] = useState<SharePagination>({ page: 1, perPage: 25, total: 0, pages: 1 });
  const [redirectActive, setRedirectActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<AdminSharesQuery>({ page: 1, perPage: 25, sort: 'sent', order: 'desc', includeRemoved: false });
  const [searchInput, setSearchInput] = useState('');
  const [detail, setDetail] = useState<{ share: AdminShare; events: ShareEvent[] } | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [testTo, setTestTo] = useState('');
  const [notice, setNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetchAdminShares(query);
      setShares(r.shares); setStats(r.stats); setPagination(r.pagination); setRedirectActive(r.settings.redirectActive);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load shares');
    } finally {
      setLoading(false);
    }
  }, [query]);
  useEffect(() => { void load(); }, [load]);

  const set = (patch: Partial<AdminSharesQuery>) => setQuery(q => ({ ...q, ...patch, page: patch.page ?? 1 }));

  const openDetail = async (id: number) => {
    try { setDetail(await fetchAdminShare(id)); } catch (e) { setNotice({ type: 'err', text: e instanceof Error ? e.message : 'Could not load the share' }); }
  };

  const act = async (share: AdminShare, action: 'resend' | 'restore' | 'remove' | 'test-send', body: Record<string, unknown> = {}) => {
    if (action === 'remove' && !window.confirm(`Remove the share to ${share.locationName}? The link stops working; comments and events are kept.`)) return;
    setBusy(share.id); setNotice(null);
    try {
      const r = await adminShareAction(action, share.id, body);
      if (r.ok) setNotice({ type: 'ok', text: action === 'test-send' ? `Sent a copy to ${body.to}.` : action === 'resend' ? `Resent via ${r.provider}${r.redirected ? ' (redirected)' : ''}.` : 'Done.' });
      else setNotice({ type: 'err', text: r.error || `${action} did not complete (${r.status ?? 'unknown'})` });
      await load();
      if (detail && detail.share.id === share.id) await openDetail(share.id);
    } catch (e) {
      setNotice({ type: 'err', text: e instanceof Error ? e.message : 'Request failed' });
    } finally {
      setBusy(null);
    }
  };

  const pages: number[] = [];
  const start = Math.max(1, pagination.page - 3);
  const end = Math.min(pagination.pages, start + 6);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div>
      {redirectActive && (
        <div className="alert alert-danger d-flex align-items-center gap-2 fw-semibold"><AlertTriangle size={18} /> Share email redirect is ON — sends are going to the test address, not to locations. <Link to="/admin/settings?tab=integrations" className="alert-link">Settings</Link></div>
      )}
      {stats && (
        <div className="row g-2 mb-3">
          {[
            { label: 'Sent today', value: stats.sentToday, icon: Send, color: 'success' },
            { label: 'Failed today', value: stats.failedToday, icon: XCircle, color: 'danger' },
            { label: 'Skipped today', value: stats.skippedToday, icon: AlertTriangle, color: 'warning' },
            { label: 'Active shares', value: stats.activeShares, icon: ListTree, color: 'dark' },
            { label: 'Opened', value: stats.viewed, icon: Eye, color: 'primary' },
            { label: 'With replies', value: stats.commented, icon: MessageSquare, color: 'info' },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="col-6 col-sm-4 col-lg">
                <div className="bg-white rounded-3 border p-3 text-center h-100">
                  <Icon size={18} className={`text-${s.color} mb-1`} />
                  <div className="fw-bold fs-4 text-dark">{s.value}</div>
                  <div className="text-muted small">{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <form onSubmit={e => { e.preventDefault(); set({ search: searchInput.trim() }); }} className="d-flex gap-2" style={{ minWidth: 280, flex: 1 }}>
            <div className="input-group input-group-sm">
              <span className="input-group-text"><Search size={14} /></span>
              <input className="form-control" placeholder="Estimate, customer, sender, location, email, sfid, guid…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-sm btn-primary">Search</button>
            {query.search && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setSearchInput(''); set({ search: '' }); }}>Clear</button>}
          </form>
          <select className="form-select form-select-sm" style={{ width: 'auto' }} value={query.kind ?? ''} onChange={e => set({ kind: e.target.value as AdminSharesQuery['kind'] })}>
            <option value="">All kinds</option><option value="retailer">Retailers</option><option value="installer">Installers</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }} value={query.sendStatus ?? ''} onChange={e => set({ sendStatus: e.target.value as AdminSharesQuery['sendStatus'] })}>
            <option value="">Any send status</option><option value="ok">Sent</option><option value="failed">Failed</option><option value="skipped">Skipped</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }} value={query.viewed ?? ''} onChange={e => set({ viewed: e.target.value as AdminSharesQuery['viewed'] })}>
            <option value="">Opened or not</option><option value="yes">Opened</option><option value="no">Not opened</option>
          </select>
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={query.from ?? ''} onChange={e => set({ from: e.target.value })} title="From" />
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={query.to ?? ''} onChange={e => set({ to: e.target.value })} title="To" />
          <div className="form-check form-switch mb-0">
            <input id="share-show-removed" type="checkbox" className="form-check-input" role="switch" checked={!!query.includeRemoved} onChange={e => set({ includeRemoved: e.target.checked })} />
            <label htmlFor="share-show-removed" className="form-check-label small text-muted">Show removed</label>
          </div>
          <select className="form-select form-select-sm ms-auto" style={{ width: 'auto' }} value={query.perPage ?? 25} onChange={e => set({ perPage: Number(e.target.value) })}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      {notice && <div className={`alert ${notice.type === 'ok' ? 'alert-success' : 'alert-danger'} py-2 small`}>{notice.text}</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0 align-middle">
            <thead className="table-tc-slate">
              <tr>
                <th>Sent</th><th>Estimate / customer</th><th>Sent to</th><th>Pricing</th><th>Delivery</th>
                <th className="text-center text-white"><Eye size={14} /></th><th className="text-center text-white"><MessageSquare size={14} /></th><th className="text-center text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr><td colSpan={8} className="text-center py-4 text-muted">Loading…</td></tr>
              ) : shares.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-4 text-muted">No shares found.</td></tr>
              ) : shares.map(s => (
                <tr key={s.id} className={s.status === 'removed' ? 'table-warning' : ''}>
                  <td className="small text-nowrap">{fmt(s.sentAt)}{s.sendCount > 1 && <div className="text-muted">×{s.sendCount}</div>}</td>
                  <td>
                    <div className="small fw-semibold">
                      <a href={`/estimate/${s.estimate.guid}?source=admin`} target="_blank" rel="noopener noreferrer">{s.estimate.name || <span className="fst-italic">Untitled</span>}</a>
                      {s.estimate.deleted && <span className="badge bg-danger ms-2">Deleted</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75em' }}>{s.sender.name || s.estimate.customerName}{(s.sender.email || s.estimate.customerEmail) && <> · {s.sender.email || s.estimate.customerEmail}</>}</div>
                  </td>
                  <td>
                    <div className="small fw-semibold d-flex align-items-center gap-1">{s.kind === 'retailer' ? <Store size={13} /> : <HardHat size={13} />} {s.locationName}
                      {s.status === 'removed' && <span className="badge bg-secondary ms-1">Removed</span>}
                      {s.promoted && <span className="badge text-bg-info ms-1">Promoted</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75em' }}>
                      {[s.location.city, s.location.region].filter(Boolean).join(', ')} · {s.location.email || 'no email'}
                      {s.location.emailOverride && <span className="text-danger"> · override → {s.location.emailOverride}</span>}
                    </div>
                  </td>
                  <td className="small">
                    {s.kind === 'installer' ? <span className="text-muted">original</span>
                      : s.retailerNow ? <>{s.retailerNow.name}{!s.retailerNow.isActive && <span className="text-warning"> (paused)</span>}</> : <span className="text-warning">unlinked</span>}
                    {s.retailerAtShare && s.retailerNow && s.retailerAtShare.id !== s.retailerNow.id && <div className="text-muted" style={{ fontSize: '0.75em' }}>was {s.retailerAtShare.name}</div>}
                  </td>
                  <td className="small">
                    <SendBadge s={s} />
                    {s.lastEvent?.error && <div className="text-danger text-truncate" style={{ fontSize: '0.75em', maxWidth: 220 }} title={s.lastEvent.error}>{s.lastEvent.error}</div>}
                  </td>
                  <td className="text-center small">{s.firstViewedAt ? <span className="text-success" title={fmt(s.firstViewedAt)}><Eye size={14} /> {s.viewCount}</span> : <span className="text-muted"><EyeOff size={14} /></span>}</td>
                  <td className="text-center small"><span className={s.commentCount > 0 ? 'fw-bold' : 'text-muted'}>{s.commentCount}</span></td>
                  <td className="text-center text-nowrap">
                    <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-1 me-1" title="Events" onClick={() => void openDetail(s.id)}><ListTree size={14} /></button>
                    {s.shareUrl && <a className="btn btn-sm btn-outline-secondary py-0 px-1 me-1" title="Open the recipient's link" href={s.shareUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /></a>}
                    {isSuperAdmin && s.status === 'active' && <button type="button" className="btn btn-sm btn-outline-primary py-0 px-1 me-1" title="Resend" disabled={busy === s.id} onClick={() => void act(s, 'resend')}>{busy === s.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>}
                    {isSuperAdmin && s.status === 'active' && <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" title="Remove" disabled={busy === s.id} onClick={() => void act(s, 'remove')}><Trash2 size={14} /></button>}
                    {isSuperAdmin && s.status === 'removed' && <button type="button" className="btn btn-sm btn-outline-success py-0 px-1" title="Restore" disabled={busy === s.id} onClick={() => void act(s, 'restore')}><RotateCcw size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-2 border-top small">
            <span className="text-muted">{pagination.total} shares</span>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" disabled={pagination.page <= 1} onClick={() => setQuery(q => ({ ...q, page: pagination.page - 1 }))}><ChevronLeft size={14} /></button>
              {pages.map(p => <button key={p} type="button" className={`btn ${p === pagination.page ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setQuery(q => ({ ...q, page: p }))}>{p}</button>)}
              <button type="button" className="btn btn-outline-secondary" disabled={pagination.page >= pagination.pages} onClick={() => setQuery(q => ({ ...q, page: pagination.page + 1 }))}><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Detail / events modal */}
      {detail && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={e => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">{detail.share.kind === 'retailer' ? <Store size={18} /> : <HardHat size={18} />} {detail.share.locationName} <span className="text-muted fw-normal fs-6">· share #{detail.share.id}</span></h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body">
                <dl className="row small mb-3">
                  <dt className="col-sm-3">Estimate</dt><dd className="col-sm-9"><a href={`/estimate/${detail.share.estimate.guid}?source=admin`} target="_blank" rel="noopener noreferrer">{detail.share.estimate.name || 'Untitled'}</a> · {detail.share.estimate.customerName} · {detail.share.estimate.customerEmail}</dd>
                  <dt className="col-sm-3">Sender</dt><dd className="col-sm-9">{detail.share.sender.name} · {detail.share.sender.email}{detail.share.sender.userId ? ` · user #${detail.share.sender.userId}` : ' · guest'}</dd>
                  <dt className="col-sm-3">Location</dt><dd className="col-sm-9">{detail.share.location.name} · sfid <span className="font-monospace">{detail.share.location.sfid}</span> · {detail.share.location.email || 'no email'}{detail.share.location.emailOverride && <span className="text-danger"> · override → {detail.share.location.emailOverride}</span>}</dd>
                  <dt className="col-sm-3">Pricing</dt><dd className="col-sm-9">{detail.share.kind === 'installer' ? 'Owner’s original pricing' : detail.share.retailerNow ? `${detail.share.retailerNow.name}${detail.share.retailerNow.isActive ? '' : ' (paused → list price)'}` : 'No linked retailer → quantities only'}{detail.share.retailerAtShare && <span className="text-muted"> · at share time: {detail.share.retailerAtShare.name}</span>}</dd>
                  <dt className="col-sm-3">Link</dt><dd className="col-sm-9 font-monospace text-break">{detail.share.shareUrl}</dd>
                  <dt className="col-sm-3">Status</dt><dd className="col-sm-9">{detail.share.status}{detail.share.removedAt && ` (${fmt(detail.share.removedAt)} by ${detail.share.removedByKind})`} · <SendBadge s={detail.share} /> · viewed {detail.share.viewCount}× · {detail.share.commentCount} replies{detail.share.promoted && ' · promoted'}</dd>
                  {detail.share.message && <><dt className="col-sm-3">Note</dt><dd className="col-sm-9" style={{ whiteSpace: 'pre-line' }}>{detail.share.message}</dd></>}
                </dl>
                {isSuperAdmin && detail.share.status === 'active' && (
                  <div className="input-group input-group-sm mb-3" style={{ maxWidth: 420 }}>
                    <span className="input-group-text"><Mail size={14} /></span>
                    <input type="email" className="form-control" placeholder="Send a copy to me (SMTP, never Pardot)…" value={testTo} onChange={e => setTestTo(e.target.value)} />
                    <button type="button" className="btn btn-outline-secondary" disabled={busy === detail.share.id || !testTo.trim()} onClick={() => void act(detail.share, 'test-send', { to: testTo.trim() })}>Send copy</button>
                  </div>
                )}
                <h6 className="fw-semibold">Events</h6>
                <div className="table-responsive">
                  <table className="table table-sm small mb-0">
                    <thead><tr><th>When</th><th>Event</th><th>Actor</th><th>Provider</th><th>To</th><th>Detail</th></tr></thead>
                    <tbody>
                      {detail.events.map(e => (
                        <tr key={e.id}>
                          <td className="text-nowrap">{fmt(e.at)}</td>
                          <td className="text-nowrap"><EventIcon type={e.type} /> {e.type}</td>
                          <td>{e.actorKind}{e.actorUserId ? ` #${e.actorUserId}` : ''}</td>
                          <td>{e.provider || ''}</td>
                          <td className="text-break">{e.effectiveTo || ''}{e.redirectReason && <span className="text-danger"> ({e.redirectReason}{e.intendedTo ? `; intended ${e.intendedTo}` : ''})</span>}</td>
                          <td className="text-break">
                            {e.httpStatus !== null && <span className="me-1">HTTP {e.httpStatus}</span>}
                            {e.error && <span className="text-danger">{e.error}</span>}
                            {e.payload && <details><summary className="text-muted">payload</summary><pre className="mb-0" style={{ fontSize: '0.7rem', maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(e.payload, null, 2)}</pre></details>}
                          </td>
                        </tr>
                      ))}
                      {detail.events.length === 0 && <tr><td colSpan={6} className="text-muted">No events.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShares;
